import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentModel,
  AuthorizationModel,
  AvailityServiceReview,
  filterAction,
  InsuranceProviderConfiguration,
  InsuranceProviderConfigurationService,
  InsuranceProviderLookup,
  Patient,
  PatientInsurance,
  updateFormValidity,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { Subject, takeUntil } from 'rxjs'

import { ElementFormData, PriorAuthorizationFormComponent } from '../../../prior-authorization-form'

export interface ServiceReviewSubmissionDialogData {
  appointment: AppointmentModel
  patient: Patient
  authorization: AuthorizationModel
  patientInsurance: PatientInsurance
  providerLookup: InsuranceProviderLookup
}

@Component({
  selector: 'mmx-service-review-submission-dialog',
  templateUrl: './service-review-submission-dialog.component.html',
  styleUrls: ['./service-review-submission-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceReviewSubmissionDialogComponent
implements OnInit, OnDestroy
{
  private onDestroy$ = new Subject<void>()
  @ViewChild(PriorAuthorizationFormComponent, { static: false })
    authForm: PriorAuthorizationFormComponent

  public form = this.fb.group({
    priorAuthorization: this.fb.control(null, Validators.required),
  })

  public formData: ElementFormData
  public submitInProgress = false

  constructor(
    public dialogRef: MatDialogRef<ServiceReviewSubmissionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ServiceReviewSubmissionDialogData,
    public insuranceProviderConfigurationService: InsuranceProviderConfigurationService,
    private fb: FormBuilder,
    private websocketService: WebSocketService,
    private snack: MatSnackBar,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.requestConfiguration(this.data.providerLookup)

    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT),
      )
      .subscribe((response) => {
        this.submitInProgress = false
        if (
          response.data?.errors ||
          (response.data as AvailityServiceReview.Review).validationMessages
        ) {
          this.cdr.detectChanges()
          this.snack.open(
            this.translate.instant('prior-authorization.form.submit-fail'),
          )
          return
        }

        this.snack.open(
          this.translate.instant('prior-authorization.form.submit-success'),
        )
        this.dialogRef.close()
      })

    this.formData = {
      patient: this.data.patient,
      carrier: this.data.providerLookup,
      appointment: this.data.appointment,
      patientInsurance: this.data.patientInsurance,
      authorization: this.data.authorization,
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onSubmit(): void {
    updateFormValidity(this.authForm.authForm.form)

    if (this.form.invalid) {
      return
    }

    this.submitInProgress = true
    const formValue = this.form.value.priorAuthorization
    const serviceReview = {
      payer: {
        id: this.data.providerLookup.provider?.slug,
      },
      ...formValue,
    }

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT,
      authorizationId: this.data.authorization.id,
      serviceReview,
      providerLookupId: this.data.providerLookup.id,
      providerId: this.data.providerLookup.provider?.id,
    })
  }

  public requestConfiguration(providerLookup: InsuranceProviderLookup) {
    this.insuranceProviderConfigurationService.configurationParams$.next({
      providerLookupId: providerLookup.id,
      payerId: providerLookup.provider?.slug,
      type: InsuranceProviderConfiguration.TYPE.SERVICE_REVIEWS,
      subtypeId: InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
    })
  }
}
